import { Renderer } from '@/game/renderer';
import { AIController } from '@/game/ai';
import { resolveAttack, updateFacing } from '@/game/physics';
import { TRex } from '@/game/fighters/trex';
import { Spinosaurus } from '@/game/fighters/spinosaurus';
import { statsApi } from '@/game/stats-api';
import { getGameConfig } from '@/game/game-config';
import type { WinStats } from '@/shared/types';
import { CANVAS_WIDTH, CANVAS_HEIGHT, MAX_HEALTH, COLORS } from '@/shared/constants';

const DEMO_ROUND_TIME = 30;
const RESET_PAUSE_FRAMES = 120;

export class TitleScene {
  private blinkTimer = 0;
  private showText = true;

  private fighter1: TRex;
  private fighter2: Spinosaurus;
  private ai1: AIController;
  private ai2: AIController;
  private roundTime = DEMO_ROUND_TIME;
  private pauseTimer = 0;
  private paused = false;
  private announceText = '';
  private hit1ThisAttack = false;
  private hit2ThisAttack = false;
  private renderer: Renderer | null = null;

  /** Cached win stats for rendering. */
  private stats: WinStats | null = null;

  constructor(private mobile: boolean = false) {
    this.fighter1 = new TRex(250, 'right');
    this.fighter2 = new Spinosaurus(CANVAS_WIDTH - 250, 'left');
    this.ai1 = new AIController('demo');
    this.ai2 = new AIController('demo');

    // Kick off initial stats fetch
    statsApi.getStats().then((s) => {
      this.stats = s;
    });
  }

  /** Bind renderer so the demo fight can spawn hit effects. */
  setRenderer(renderer: Renderer): void {
    this.renderer = renderer;
  }

  private resetFight(): void {
    this.fighter1.reset(250, 'right');
    this.fighter2.reset(CANVAS_WIDTH - 250, 'left');
    this.roundTime = DEMO_ROUND_TIME;
    this.paused = false;
    this.pauseTimer = 0;
    this.announceText = '';
    this.hit1ThisAttack = false;
    this.hit2ThisAttack = false;
    // Reset fighter display transforms so KO animations + grayscale filter don't persist
    this.renderer?.resetFighterDisplays('title-');
    this.renderer?.hitEffects.clear();
    this.renderer?.applyScreenShake(0, 0);
  }

  update(): void {
    this.blinkTimer++;
    if (this.blinkTimer >= 30) {
      this.blinkTimer = 0;
      this.showText = !this.showText;
    }

    if (this.paused) {
      this.pauseTimer--;

      // Let hit effects finish their animation
      if (this.renderer) {
        const shake = this.renderer.hitEffects.update();
        this.renderer.applyScreenShake(shake.shakeX, shake.shakeY);
      }

      // Check for KO crumble particle spawning
      this.checkKOCrumble();

      if (this.pauseTimer <= 0) {
        this.resetFight();
      }
      return;
    }

    this.roundTime -= 1 / 60;

    const input1 = this.ai1.update(this.fighter1, this.fighter2);
    const input2 = this.ai2.update(this.fighter2, this.fighter1);

    this.fighter1.applyInput(input1);
    this.fighter2.applyInput(input2);

    this.fighter1.update();
    this.fighter2.update();

    updateFacing(this.fighter1, this.fighter2);

    if (this.fighter1.isAttacking) {
      if (!this.hit1ThisAttack) {
        const hit = resolveAttack(this.fighter1, this.fighter2);
        if (hit) {
          this.hit1ThisAttack = true;
          this.renderer?.hitEffects.spawn(hit.x, hit.y, hit.attackType);
        }
      }
    } else {
      this.hit1ThisAttack = false;
    }

    if (this.fighter2.isAttacking) {
      if (!this.hit2ThisAttack) {
        const hit = resolveAttack(this.fighter2, this.fighter1);
        if (hit) {
          this.hit2ThisAttack = true;
          this.renderer?.hitEffects.spawn(hit.x, hit.y, hit.attackType);
        }
      }
    } else {
      this.hit2ThisAttack = false;
    }

    // Update hit effects and apply screen shake
    if (this.renderer) {
      const shake = this.renderer.hitEffects.update();
      this.renderer.applyScreenShake(shake.shakeX, shake.shakeY);
    }

    if (this.fighter1.health <= 0 || this.fighter2.health <= 0 || this.roundTime <= 0) {
      this.paused = true;
      this.pauseTimer = RESET_PAUSE_FRAMES;

      const config = getGameConfig();
      if (this.fighter1.health > this.fighter2.health) {
        this.announceText = `${config.p1Name} WINS!`;
        statsApi.recordWin(config.p1StatsKey).then((s) => {
          this.stats = s;
        });
      } else if (this.fighter2.health > this.fighter1.health) {
        this.announceText = `${config.p2Name} WINS!`;
        statsApi.recordWin(config.p2StatsKey).then((s) => {
          this.stats = s;
        });
      } else {
        this.announceText = 'DRAW!';
      }
    }
  }

  /**
   * Check both fighters for KO crumble state and spawn crumble particles.
   */
  private checkKOCrumble(): void {
    if (!this.renderer) return;

    if (this.fighter1.state === 'ko') {
      this.renderer.checkAndSpawnCrumble('title-p1', this.fighter1.x, this.fighter1.y);
    }
    if (this.fighter2.state === 'ko') {
      this.renderer.checkAndSpawnCrumble('title-p2', this.fighter2.x, this.fighter2.y);
    }
  }

  draw(renderer: Renderer): void {
    const config = getGameConfig();

    // Background and ground are handled by the PixiJS renderer layers

    renderer.drawHealthBar(20, 20, CANVAS_WIDTH / 2 - 60, this.fighter1.health, MAX_HEALTH);
    renderer.drawHealthBar(
      CANVAS_WIDTH / 2 + 40,
      20,
      CANVAS_WIDTH / 2 - 60,
      this.fighter2.health,
      MAX_HEALTH,
    );

    renderer.drawText(config.p1Name, 120, 60, { font: '14px monospace', color: config.p1Color });
    renderer.drawText(config.p2Name, CANVAS_WIDTH - 120, 60, {
      font: '14px monospace',
      color: config.p2Color,
    });

    // Draw fighters with full state info for PixiJS animation
    renderer.drawFighter(
      this.fighter1.x,
      this.fighter1.y,
      this.fighter1.width,
      this.fighter1.height,
      this.fighter1.state === 'hit' ? '#ffffff' : this.fighter1.color,
      this.fighter1.facing,
      '',
      this.fighter1.state,
      this.fighter1.attackTimer,
      this.fighter1.currentAttack,
      'title-p1',
      'trex',
    );
    renderer.drawFighter(
      this.fighter2.x,
      this.fighter2.y,
      this.fighter2.width,
      this.fighter2.height,
      this.fighter2.state === 'hit' ? '#ffffff' : this.fighter2.color,
      this.fighter2.facing,
      '',
      this.fighter2.state,
      this.fighter2.attackTimer,
      this.fighter2.currentAttack,
      'title-p2',
      'spinosaurus',
    );

    // Draw hit effect particles
    renderer.hitEffects.draw();

    // Title text with shadow effect
    renderer.drawText(config.title, CANVAS_WIDTH / 2, 160, {
      font: 'bold 72px monospace',
      color: COLORS.title,
    });

    renderer.drawText(config.subtitle, CANVAS_WIDTH / 2, 210, {
      font: 'bold 28px monospace',
      color: COLORS.subtitle,
    });

    if (this.announceText) {
      renderer.drawText(this.announceText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 20, {
        font: 'bold 48px monospace',
        color: COLORS.title,
      });
    }

    // --- Win Stats leaderboard (top-right corner) ---
    this.drawStats(renderer);

    const startText = this.mobile ? 'TAP TO START' : 'PRESS ENTER TO START';
    if (this.showText) {
      renderer.drawText(startText, CANVAS_WIDTH / 2, CANVAS_HEIGHT - 50, {
        font: '20px monospace',
        color: COLORS.text,
      });
    }

    if (!this.mobile) {
      renderer.drawText(
        'WASD/Arrows: Move  |  J: Bite  |  K: Tail Swipe  |  L: Special  |  Shift: Block',
        CANVAS_WIDTH / 2,
        CANVAS_HEIGHT - 20,
        { font: '12px monospace', color: '#888888' },
      );
    }
  }

  private drawStats(renderer: Renderer): void {
    if (!this.stats) return;

    const config = getGameConfig();
    const x = CANVAS_WIDTH - 110;
    const baseY = 100;

    renderer.drawText('TOP WINS', x, baseY, {
      font: 'bold 14px monospace',
      color: COLORS.subtitle,
    });

    // Sort by wins descending
    const p1Key = config.p1StatsKey;
    const p2Key = config.p2StatsKey;
    const entries: [string, number, string][] = [
      [config.p1Name, this.stats[p1Key], config.p1Color],
      [config.p2Name, this.stats[p2Key], config.p2Color],
    ];
    entries.sort((a, b) => b[1] - a[1]);

    entries.forEach(([name, wins, color], i) => {
      const y = baseY + 22 + i * 20;
      renderer.drawText(`${name} ${wins}`, x, y, {
        font: '13px monospace',
        color,
      });
    });
  }
}
